<template>
  <div
    class="ag-card-pro"
    :class="{ 'ag-card-pro--next': isNext }"
  >
    <div class="flex justify-between items-start gap-3">
      <div class="flex gap-3 min-w-0 flex-1">
        <div class="ag-card-pro__date-box shrink-0">
          <span class="ag-card-pro__day">{{ dayPart }}</span>
          <span class="ag-card-pro__month">{{ monthPart }}</span>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2 mb-1">
            <span
              class="ag-card-pro__status-badge"
              :class="`ag-card-pro__status-badge--${statusKey}`"
            >
              {{ statusLabel }}
            </span>
          </div>
          <p class="ag-card-pro__label">
            {{ t('kanban.taskDetail.sections.schedule.scheduledFor') }}
          </p>
          <p class="ag-card-pro__date-full">{{ fullDate }}</p>
          <p class="ag-card-pro__time flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            {{ agendamento.horario }}
          </p>
          <p v-if="agendamento.motivo" class="ag-card-pro__motivo mt-2 flex items-center gap-2">
            <span class="ag-card-pro__motivo-badge">
              {{ t('kanban.taskDetail.sections.schedule.scheduled') }}
            </span>
            {{ agendamento.motivo }}
          </p>
          <div v-if="showActions && isAgendado" class="ag-card-pro__actions mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              class="ag-card-pro__action ag-card-pro__action--success"
              @click="emitStatus('REALIZADO')"
            >
              <svg class="ag-card-pro__action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              {{ t('workspaceClienteModal.agendamento.markRealizado') }}
            </button>
            <button
              type="button"
              class="ag-card-pro__action ag-card-pro__action--warning"
              @click="emitStatus('REMARCADO')"
            >
              <svg class="ag-card-pro__action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              {{ t('workspaceClienteModal.agendamento.markRemarcado') }}
            </button>
            <button
              type="button"
              class="ag-card-pro__action ag-card-pro__action--danger"
              @click="emitStatus('CANCELADO')"
            >
              <svg class="ag-card-pro__action-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              {{ t('workspaceClienteModal.agendamento.markCancelado') }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="isNext || showDelete" class="ag-card-pro__aside shrink-0">
        <span v-if="isNext" class="ag-card-pro__ribbon">{{ t('workspaceClienteModal.agendamento.next') }}</span>
        <ClienteIconBtn
          v-if="showDelete"
          variant="delete"
          @click="emit('delete')"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ClienteIconBtn from './ClienteIconBtn.vue'

const props = defineProps({
  agendamento: { type: Object, required: true },
  isNext: { type: Boolean, default: false },
  locale: { type: String, default: 'pt-BR' },
  showActions: { type: Boolean, default: true },
  showDelete: { type: Boolean, default: false }
})

const emit = defineEmits(['status-change', 'delete'])

const { t } = useI18n()

const statusKey = computed(() => {
  const s = String(props.agendamento.status || 'AGENDADO').trim().toUpperCase()
  const map = {
    AGENDADO: 'agendado',
    REALIZADO: 'realizado',
    REMARCADO: 'remarcado',
    CANCELADO: 'cancelado'
  }
  return map[s] || 'agendado'
})

const isAgendado = computed(() => statusKey.value === 'agendado')

const statusLabel = computed(() => {
  const key = `workspaceClienteModal.agendamento.status${statusKey.value.charAt(0).toUpperCase()}${statusKey.value.slice(1)}`
  return t(key)
})

const parseDate = () => {
  const raw = props.agendamento.dataAgendamento
  if (!raw) return null
  const d = new Date(String(raw).includes('T') ? raw : `${raw}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

const dayPart = computed(() => {
  const d = parseDate()
  return d ? String(d.getDate()).padStart(2, '0') : '—'
})

const monthPart = computed(() => {
  const d = parseDate()
  if (!d) return ''
  return d.toLocaleDateString(props.locale, { month: 'short' }).replace('.', '')
})

const fullDate = computed(() => {
  const d = parseDate()
  if (!d) return props.agendamento.dataAgendamento || '—'
  return d.toLocaleDateString(props.locale, { day: '2-digit', month: 'long', year: 'numeric' })
})

function emitStatus (status) {
  emit('status-change', { id: props.agendamento.id, status })
}
</script>

<style scoped>
.ag-card-pro {
  position: relative;
  padding: 1rem 1.25rem;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  margin-bottom: 0.75rem;
  overflow: hidden;
}

.ag-card-pro__ribbon {
  display: inline-block;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: var(--info);
  color: #fff;
  white-space: nowrap;
}

.ag-card-pro__aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.375rem;
}

.ag-card-pro__date-box {
  align-self: flex-start;
  width: 3rem;
  text-align: center;
  padding: 0.5rem 0.25rem;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--info) 42%, var(--border-primary));
  background: color-mix(in srgb, var(--info) 20%, var(--bg-tertiary));
}

.ag-card-pro--next .ag-card-pro__date-box {
  background: color-mix(in srgb, var(--info) 30%, var(--bg-card));
  border-color: color-mix(in srgb, var(--info) 55%, var(--border-primary));
}

.ag-card-pro__day {
  display: block;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
}

.ag-card-pro__month {
  display: block;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
}

.ag-card-pro__status-badge {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
}

.ag-card-pro__status-badge--agendado {
  background: color-mix(in srgb, var(--info) 22%, var(--bg-tertiary));
  color: var(--info);
}

.ag-card-pro__status-badge--realizado {
  background: color-mix(in srgb, var(--success) 22%, var(--bg-tertiary));
  color: var(--success);
}

.ag-card-pro__status-badge--remarcado {
  background: color-mix(in srgb, var(--warning, #f59e0b) 22%, var(--bg-tertiary));
  color: var(--warning, #d97706);
}

.ag-card-pro__status-badge--cancelado {
  background: color-mix(in srgb, var(--error) 18%, var(--bg-tertiary));
  color: var(--error);
}

.ag-card-pro__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
  margin: 0;
}

.ag-card-pro__date-full {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0.125rem 0;
}

.ag-card-pro__time {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--info);
  margin: 0;
}

.ag-card-pro__motivo {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
}

.ag-card-pro__motivo-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: color-mix(in srgb, var(--success) 20%, var(--bg-tertiary));
  color: var(--text-primary);
}

.ag-card-pro__action {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.4375rem 0.75rem;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}

.ag-card-pro__action-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.ag-card-pro__action:hover {
  background: var(--bg-hover);
}

.ag-card-pro__action:active {
  transform: translateY(1px);
}

.ag-card-pro__action--success {
  border-color: color-mix(in srgb, var(--success) 40%, transparent);
  color: var(--success);
  background: color-mix(in srgb, var(--success) 10%, var(--bg-secondary));
}

.ag-card-pro__action--success:hover {
  background: color-mix(in srgb, var(--success) 18%, var(--bg-secondary));
}

.ag-card-pro__action--warning {
  border-color: color-mix(in srgb, var(--warning, #f59e0b) 40%, transparent);
  color: var(--warning, #d97706);
  background: color-mix(in srgb, var(--warning, #f59e0b) 10%, var(--bg-secondary));
}

.ag-card-pro__action--warning:hover {
  background: color-mix(in srgb, var(--warning, #f59e0b) 18%, var(--bg-secondary));
}

.ag-card-pro__action--danger {
  border-color: color-mix(in srgb, var(--error) 40%, transparent);
  color: var(--error);
  background: color-mix(in srgb, var(--error) 8%, var(--bg-secondary));
}

.ag-card-pro__action--danger:hover {
  background: color-mix(in srgb, var(--error) 16%, var(--bg-secondary));
}
</style>
